"use client";

/**
 * The affiliate buttons on public pages: "Join as an affiliate" + "Sign in", or "Go to your affiliate
 * dashboard" once this browser is signed in to the affiliate portal. Same buttons as the rest of the site.
 */
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ArrowRight, LogIn } from "lucide-react";
import { Button } from "@/components/Button";
import { Portal } from "@/components/app/Portal";
import { PortalOpening } from "@/components/affiliate/PortalOpening";
import { affToken, warmAffPortal } from "@/lib/affiliatePortal";

export function AffiliateActions({ joinLabel = "Join as an affiliate", onViolet = false }: { joinLabel?: string; onViolet?: boolean }) {
  const router = useRouter();
  const [signedIn, setSignedIn] = useState(false);
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    const yes = !!affToken();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the portal token lives in this browser only
    setSignedIn(yes);
    if (!yes) return;
    // Signed in: get the portal's page code and numbers ready now, so opening it is (nearly) instant.
    router.prefetch("/affiliate/dashboard");
    // The numbers only on the affiliate pages; elsewhere (the home page highlight) they wait for the pointer.
    if (window.location.pathname.startsWith("/affiliate")) warmAffPortal();
  }, [router]);
  // Coming back with the browser's back button restores this page from memory: drop the cover.
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => { if (e.persisted) setOpening(false); };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  if (signedIn)
    return (
      <>
        <span
          className="contents"
          onClickCapture={(e) => {
            // A plain click: cover the website with the portal's opening screen right away (the portal page shows
            // the same screen until its numbers arrive, so the move is seamless). New-tab clicks are left alone.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            setOpening(true);
          }}
          onPointerEnter={warmAffPortal}
        >
          <Button href="/affiliate/dashboard" variant={onViolet ? "light" : "violet"}>
            Go to your affiliate dashboard <ArrowRight size={17} />
          </Button>
        </span>
        {opening && (
          <Portal>
            <PortalOpening cover />
          </Portal>
        )}
      </>
    );
  return (
    <>
      <Button href="/affiliate/signup" variant={onViolet ? "light" : "violet"}>
        {joinLabel} <ArrowRight size={17} />
      </Button>
      {onViolet ? (
        // On the violet header: a white outline, like the secondary buttons on the site's other coloured cards.
        <Link
          href="/affiliate/login"
          className="inline-flex h-[52px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border-[1.5px] border-white/60 px-6 sm:w-auto sm:px-7 text-[0.94rem] font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-white/15"
        >
          <LogIn size={17} /> Sign in
        </Link>
      ) : (
        <Button href="/affiliate/login" variant="light">
          <LogIn size={17} /> Sign in
        </Button>
      )}
    </>
  );
}
